import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CtaBanner from '../components/CtaBanner';
import kn62Image from '../assets/products/aidashcam/kn62.jpg';
import ks62Image from '../assets/products/aidashcam/ks62.jpg';
import ks21Image from '../assets/products/aidashcam/ks21.jpg';

const CATEGORIES = [
  { id: 'aidashcam', label: 'AI DashCam' },
  { id: 'dashcam', label: 'DashCam' },
  { id: 'gps', label: 'GPS Tracker' },
  { id: 'solar', label: 'Solar Cam' },
  { id: 'accessaries', label: 'Accessaries' },
];

const AI_DASHCAM_MODELS = [
  {
    id: 'kn62', name: 'KN62', tag: 'AI DashCam · Front & Cabin', image: kn62Image, gallery: [],
    desc: 'Dual-lens recording with driver monitoring built in, covering the road ahead and inside the cabin.',
    longDesc: 'The KN62 pairs a road-facing lens with an inward-facing driver monitor, so every trip is covered both outside the vehicle and in. Built on our own AI safety core, it pairs with the KLIK mobile app for live status and playback.',
    specs: ['2K Front Recording', 'DMS Driver Monitoring', 'ADAS Alerts', 'Wi-Fi App Control'],
  },
  {
    id: 'ks62', name: 'KS62', tag: 'AI DashCam · Front & Rear', image: ks62Image, gallery: [],
    desc: 'Simultaneous front and rear recording with live tracking, for full coverage in every direction.',
    longDesc: 'The KS62 records front and rear simultaneously and streams live location, so you always know where the vehicle is and what happened on the road, in both directions. Available with a full accessory kit — dual cabin lenses, GPS module and SOS button.',
    specs: ['2K Front + 1080P Rear', '4G Live Tracking', 'ADAS Alerts', 'Loop Recording'],
  },
  {
    id: 'ks21', name: 'KS21', tag: 'AI DashCam · Compact', image: ks21Image, gallery: [],
    desc: 'A compact single-channel dash cam built for everyday driving, with the same AI safety core.',
    longDesc: 'The KS21 brings our AI safety core into a compact single-channel body, built for everyday driving where a smaller footprint matters without giving up ADAS and night visibility.',
    specs: ['2K Front Recording', 'ADAS Alerts', 'Night Vision', 'Loop Recording'],
  },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat');
  const activeTab = CATEGORIES.some((c) => c.id === catParam) ? catParam : 'aidashcam';
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [activeTab]);

  const activeCategory = CATEGORIES.find((c) => c.id === activeTab) || CATEGORIES[0];
  const selectedModel = AI_DASHCAM_MODELS.find((m) => m.id === selected) || null;

  const selectTab = (id) => {
    setSearchParams(id === 'aidashcam' ? {} : { cat: id });
  };

  return (
    <div className="kh">
      <Header active="products" />

      <section className="kh-page-banner">
        <div className="kh-glow" style={{ width: 420, height: 420, top: -180, left: -100 }} />
        <div className="kh-glow kh-glow-b" style={{ width: 360, height: 360, top: -140, right: -120 }} />
        <div className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', position: 'relative', textAlign: 'center' }}>
          <div className="kh-fade-1 kh-breadcrumb">
            <a href="/">Home</a><span>/</span><span style={{ color: 'var(--text)' }}>Products</span>
          </div>
          <h1 className="kh-fade-2 kh-page-title">Our Products</h1>
          <p className="kh-fade-3 kh-page-sub" style={{ maxWidth: 620 }}>AI-powered vehicle safety hardware &mdash; dash cams, GPS trackers and solar CCTV &mdash; designed, built and controlled end-to-end by our own team.</p>
        </div>
      </section>

      <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '56px 24px 0' }}>
        <div className="kh-tab-bar">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`kh-tab${activeTab === c.id ? ' active' : ''}`}
              onClick={() => selectTab(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === 'aidashcam' ? (
        <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 24px 40px' }}>
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 40px' }}>
            <div className="kh-eyebrow"><span className="kh-dot" />AI DASHCAM</div>
            <h2 className="kh-h2" style={{ fontSize: 26, marginTop: 16 }}>Three models, one AI safety core</h2>
            <p style={{ fontSize: 14.5, color: 'var(--muted)', marginTop: 10, lineHeight: 1.7 }}>Click any model to see full specs and more photos.</p>
          </div>
          <div className="kh-product-grid">
            {AI_DASHCAM_MODELS.map((m) => (
              <button className="kh-product-card" key={m.id} onClick={() => setSelected(m.id)}>
                <div className="kh-product-image">
                  {m.image ? (
                    <img src={m.image} alt={m.name} />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                      <div className="kh-product-image-badge"><svg width="32" height="32"><use href="#i-dashcam" /></svg></div>
                      <span className="kh-product-image-note">Photo coming soon</span>
                    </div>
                  )}
                </div>
                <div className="kh-product-body">
                  <div className="kh-product-tag">{m.tag}</div>
                  <h3 className="kh-product-name">{m.name}</h3>
                  <p className="kh-product-desc">{m.desc}</p>
                  <div className="kh-product-specs">
                    {m.specs.map((spec) => (
                      <span className="kh-spec-chip" key={spec}>{spec}</span>
                    ))}
                  </div>
                </div>
                <div className="kh-product-cta">View Full Details <svg width="14" height="14"><use href="#i-arrow" /></svg></div>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section className="kh-container" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 24px 90px' }}>
          <div className="kh-comingsoon">
            <div className="kh-icon-badge" style={{ margin: '0 auto', width: 64, height: 64, borderRadius: 18 }}><svg width="26" height="26"><use href="#i-cctv" /></svg></div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginTop: 20, color: 'var(--text)' }}>{activeCategory.label} &mdash; coming soon</h3>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginTop: 10, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7 }}>We're building this category next. Check back soon, or ask us directly.</p>
          </div>
        </section>
      )}

      <CtaBanner />
      <Footer />

      {selectedModel && (
        <div className="kh-detail-overlay" onClick={() => setSelected(null)}>
          <div className="kh-detail-panel" onClick={(e) => e.stopPropagation()}>
            <button className="kh-detail-close" onClick={() => setSelected(null)}><svg width="16" height="16"><use href="#i-close" /></svg></button>
            <div className="kh-detail-visual">
              {selectedModel.image ? (
                <img src={selectedModel.image} alt={selectedModel.name} />
              ) : (
                <div className="kh-detail-visual-badge"><svg width="62" height="62"><use href="#i-dashcam" /></svg></div>
              )}
            </div>
            <div className="kh-detail-info">
              <div className="kh-product-tag">{selectedModel.tag}</div>
              <h2 style={{ fontSize: 28, fontWeight: 700, marginTop: 8, color: 'var(--text)' }}>{selectedModel.name}</h2>
              <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginTop: 12 }}>{selectedModel.longDesc}</p>

              <p className="kh-detail-section-label">Key Specs</p>
              <div className="kh-product-specs" style={{ marginTop: 10 }}>
                {selectedModel.specs.map((spec) => (
                  <span className="kh-spec-chip" key={spec}>{spec}</span>
                ))}
              </div>

              <p className="kh-detail-section-label">More Photos</p>
              <div className="kh-detail-gallery">
                {Array.from({ length: 4 }).map((_, i) => {
                  const galleryImage = selectedModel.gallery[i];
                  return (
                    <div className="kh-detail-thumb" key={i}>
                      {galleryImage ? (
                        <img src={galleryImage} alt={`${selectedModel.name} extra ${i + 1}`} />
                      ) : (
                        <svg width="16" height="16"><use href="#i-plus" /></svg>
                      )}
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 8 }}>
                {selectedModel.image ? 'More angles and accessory photos coming soon.' : 'Product photos coming soon.'}
              </p>

              <div style={{ display: 'flex', gap: 12, marginTop: 30, flexWrap: 'wrap' }}>
                <a href="tel:0717123790" className="kh-btn-primary" style={{ padding: '12px 22px' }}>
                  <svg width="15" height="15"><use href="#i-phone" /></svg>Request a Quote
                </a>
                <a href="https://wa.me/94717123790" target="_blank" rel="noopener noreferrer" className="kh-btn-ghost" style={{ padding: '12px 22px' }}>
                  <svg width="15" height="15"><use href="#i-wa" /></svg>WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
